// The Dock's menu, drawn over the screen while open: the Dock grown from the
// round button, and the panel "⋯" opens above the Dock's row (one surface).
//
// Promise:
// - From the round button the Dock grows to the left, over what the page put
//   on the Dock (which fades and cannot be touched meanwhile), as wide as the
//   resting Dock would be. From the resting Dock's "⋯" the panel grows up from
//   the same surface, the Dock's row staying at its bottom.
// - The panel lists, from the top: what goes on (the parts' `Ongoing`: the
//   call one is in), the account (its menu: settings and the host's items, no
//   shortcuts on a phone) and the way to rearrange the Dock; Me's places with their
//   names (those in the Dock too) and Me's favorites; then the host's place
//   (its name opens its menu: other places, its settings) and its places.
// - There is no close button: a press outside (it reaches nothing below),
//   Escape, going somewhere, and the browser's back close it. Closing from the
//   panel opened by the round button folds the Dock back too.
// - Nothing covers the screen (iOS 26 colors its bars from fixed elements at
//   the screen's edge, 2026-09-30), and the screen behind is not dimmed
//   (2026-10-04); the finger cannot move it while the panel is open.
// - Moves take 200 ms, ease-out (none with reduced motion). Opening from the
//   round button, the surface grows out of the button's box to the left, the
//   items rising into sight. Closing, it folds back into where it came from
//   (the round button pressed or the resting Dock), the opening backwards,
//   and is taken away after; a new
//   opening meanwhile starts a new layer. The glass is never faded nor clipped
//   (the blur behind it stops working); only its width and place move.
import { Link, useNavigate, useRouter } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import {
  Fragment,
  type MouseEvent,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useMeApp } from "../app/context";
import { useShownAccount } from "../me/account";
import { useFavorites } from "../me/favorites";
import { AccountQuickSettings } from "../shell/account-quick";
import { OngoingRows } from "../shell/ongoing";
import { menuItemsOf, menuLists, useShellMenu } from "../shell/outside/source";
import type { DockPlace } from "../shell/phone";
import { Avatar } from "../ui/bits";
import { GLASS } from "../ui/glass";
import { Icon } from "../ui/icon";
import { MenuButton } from "../ui/menu";
import { type MenuState, usePhoneState } from "./context";
import { DOCK_HEIGHT, DockRow, EntryBadge, UnreadDot } from "./dock-row";
import { leaveOverlayEntry, pushOverlayEntry } from "./history";
import { type DockEntry, fitCapacity } from "./items";

const MOTION = { duration: 200, easing: "ease-out" } as const;

const reducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

const keepFocus = (e: MouseEvent) => e.preventDefault();

/** Between the page's things on the Dock's row and the Dock grown over them. */
const SQUEEZE_GAP = 7;
/** Squeezed, the page's things fade in the last 4 tenths of opening (no thin sliver is left)… */
const SQUEEZE_OUT: Keyframe[] = [
  { offset: 0, opacity: 1 },
  { offset: 0.6, opacity: 1 },
  { offset: 1, opacity: 0 },
];
/** …and come back in the first 4 tenths of closing. */
const SQUEEZE_IN: Keyframe[] = [
  { offset: 0, opacity: 0 },
  { offset: 0.4, opacity: 1 },
  { offset: 1, opacity: 1 },
];

/**
 * Squeezes what the page put on the Dock's row from the right as the Dock's
 * left edge moves from `from` to `to` (the same timing as the Dock: both
 * edges move together on every frame). Its right edge stays 7 px left of the
 * Dock's. A glass surface (the page's tools, `data-phone-squeeze="width"`)
 * narrows: a clip on glass stops the blur behind it. A post box
 * (`data-phone-squeeze="clip"`) is clipped, never resized: its size changing
 * moved the caret away from where WebKit had put it.
 */
function squeeze(
  from: number,
  to: number,
  fade: Keyframe[],
  timing: KeyframeAnimationOptions,
): Animation[] {
  const out: Animation[] = [];
  for (const el of document.querySelectorAll<HTMLElement>(
    "[data-phone-squeeze]",
  )) {
    if (!el.animate || el.getClientRects().length === 0) continue;
    const r = el.getBoundingClientRect();
    const edge = (p: number) =>
      Math.min(r.right, Math.max(r.left, from + (to - from) * p - SQUEEZE_GAP));
    // Where the edge crosses the box's sides, a stop of its own (the edge is
    // held at the side outside them).
    const offsets = [0, 1];
    if (to !== from)
      for (const side of [r.left, r.right]) {
        const p = (side + SQUEEZE_GAP - from) / (to - from);
        if (p > 0 && p < 1) offsets.push(p);
      }
    const stops = offsets.sort((a, b) => a - b);
    const frames =
      el.dataset.phoneSqueeze === "width"
        ? stops.map((offset) => ({
            offset,
            width: `${edge(offset) - r.left}px`,
          }))
        : stops.map((offset) => ({
            offset,
            clipPath: `inset(-8px ${r.right - edge(offset)}px -8px -8px round 24px)`,
          }));
    const overflow = el.style.overflow;
    if (el.dataset.phoneSqueeze === "width") el.style.overflow = "hidden";
    const shape = startNow(el, frames, timing);
    const restore = () => {
      el.style.overflow = overflow;
    };
    shape.addEventListener("finish", restore);
    shape.addEventListener("cancel", restore);
    out.push(shape, startNow(el, fade, timing));
  }
  return out;
}

const shown = (el: HTMLElement | null) =>
  el?.isConnected && el.getClientRects().length > 0 ? el : null;

/**
 * The surface (its right and bottom kept at the Dock's) shown as another box:
 * its width and place only.
 */
function shapeOf(surface: HTMLElement, box: HTMLElement): Keyframe {
  const s = surface.getBoundingClientRect();
  const b = box.getBoundingClientRect();
  return {
    width: `${b.width}px`,
    transform: `translate(${b.right - s.right}px, ${b.bottom - s.bottom}px)`,
  };
}

/**
 * A move whose first frame is also written inline for as long as it runs; at
 * its end (or cancelled) the inline style goes and the move with it, in the
 * same task. iOS's WebKit painted a frame without the move just after it was
 * asked for: the screen's own look at that moment, which is where the move
 * ends (the panel showed whole, then jumped back to the Dock and grew: the
 * person, 2026-10-18, "jerky"; closing, it showed folded for a frame before
 * it folded). With the start written under it, such a frame shows the start.
 * A running move wins over inline styles, so they change nothing meanwhile.
 */
function startNow(
  el: HTMLElement,
  frames: Keyframe[],
  timing: KeyframeAnimationOptions,
): Animation {
  // Held at its end until the inline start is taken off with it.
  const move = el.animate(frames, { ...timing, fill: "forwards" });
  const first = Object.entries(frames[0] ?? {})
    .filter(([p]) => p !== "offset" && p !== "easing" && p !== "composite")
    .map(
      ([p, v]) =>
        [p.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`), String(v)] as const,
    );
  const before = first.map(([p]) => [p, el.style.getPropertyValue(p)] as const);
  for (const [p, v] of first) el.style.setProperty(p, v);
  let done = false;
  const restore = () => {
    if (done) return;
    done = true;
    for (const [p, v] of before)
      if (v) el.style.setProperty(p, v);
      else el.style.removeProperty(p);
    move.cancel();
  };
  move.addEventListener("finish", restore);
  move.addEventListener("cancel", restore);
  return move;
}

export function MenuLayer({
  menu,
  closing,
  onExited,
  arrange,
  place,
}: {
  /** What is open (or, closing, what was). */
  menu: NonNullable<MenuState>;
  /** Folding back: drawn until the move ends, then `onExited`. */
  closing: boolean;
  onExited(): void;
  /** The Dock's entries for a row of that many. */
  arrange(capacity: number): {
    all: readonly DockEntry[];
    dock: readonly DockEntry[];
  };
  place: DockPlace | null;
}) {
  const state = usePhoneState();
  const box = menu.box;
  const width = box.width;
  const { all, dock } = arrange(fitCapacity(width, arrange));
  const here = state?.here ?? null;
  const router = useRouter();
  const dialog = useRef<HTMLDivElement>(null);
  const surface = useRef<HTMLDivElement>(null);
  const cell = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const row = useRef<HTMLDivElement>(null);
  // Closing, the panel folds down with the rest.
  const panel = menu.panel && !closing;
  const close = closing ? undefined : state?.closeMenu;
  const pointer = useRef(menu.pointer);
  // Where it grows from and folds back into: the button pressed while it is
  // still shown, else the one there now (an item went to another screen).
  const opener = menu.opener;
  const menuButton = state?.menuButton;
  const target = () =>
    shown(opener) ?? shown(menuButton?.current ?? null) ?? null;
  // The panel's content stays drawn once opened (it shrinks with it).
  const [panelDrawn, setPanelDrawn] = useState(panel);
  if (panel && !panelDrawn) setPanelDrawn(true);

  // The browser's back closes it; closing by other means leaves the entry.
  // frontend-development: not a fetch; the history entry of an overlay.
  useEffect(() => {
    if (!close) return;
    const history = router.history;
    pushOverlayEntry(history);
    let open = true;
    const off = history.subscribe(() => {
      if (
        open &&
        !(history.location.state as Record<string, unknown>)?.trameriaOverlay
      )
        close();
    });
    return () => {
      open = false;
      off();
      leaveOverlayEntry(history);
    };
  }, [router, close]);

  // A press outside closes it and reaches nothing below (no layer covers the
  // screen: the document hears it first).
  // frontend-development: not a fetch; the outside press of an overlay.
  useEffect(() => {
    if (!close) return;
    const outside = (e: Event) =>
      e.target instanceof Node &&
      !dialog.current?.contains(e.target) &&
      // Menus the panel opened are portaled outside it.
      !(e.target instanceof Element && e.target.closest("[role=menu]"));
    const swallow = (e: Event) => {
      if (!outside(e)) return;
      pointer.current = true;
      e.stopPropagation();
      if (e.type === "mousedown") e.preventDefault();
    };
    const onClick = (e: Event) => {
      if (!outside(e)) return;
      e.preventDefault();
      e.stopPropagation();
      close?.();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) {
        e.preventDefault();
        close?.();
      }
    };
    document.addEventListener("pointerdown", swallow, true);
    document.addEventListener("mousedown", swallow, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", swallow, true);
      document.removeEventListener("mousedown", swallow, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [close]);

  // While the panel is open the finger moves only its list (iOS hands an
  // overscroll to the page behind, and locking the body brought Safari's bar
  // back, 2026-09-30).
  // frontend-development: not a fetch; holds the screen behind the panel.
  useEffect(() => {
    if (!panel) return;
    let lastY: number | null = null;
    const listOf = (e: Event) => {
      const list = panelRef.current?.querySelector<HTMLElement>(
        "[data-phone-menu-list]",
      );
      return list &&
        e.target instanceof Node &&
        list.contains(e.target) &&
        list.scrollHeight > list.clientHeight
        ? list
        : null;
    };
    const start = (e: TouchEvent) => {
      lastY = e.touches[0]?.clientY ?? null;
    };
    const move = (e: TouchEvent) => {
      const list = listOf(e);
      const y = e.touches[0]?.clientY ?? null;
      const dy = y !== null && lastY !== null ? y - lastY : 0;
      lastY = y;
      if (!list) return e.preventDefault();
      const atTop = list.scrollTop <= 0;
      const atEnd = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
      if ((atTop && dy > 0) || (atEnd && dy < 0)) e.preventDefault();
    };
    document.addEventListener("touchstart", start, { passive: true });
    document.addEventListener("touchmove", move, { passive: false });
    return () => {
      document.removeEventListener("touchstart", start);
      document.removeEventListener("touchmove", move);
    };
  }, [panel]);

  // Opened from the round button: the surface grows from it to the left. The
  // layer is mounted once per opening, so the move runs once.
  // frontend-development: not a fetch; the opening move.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per opening
  useLayoutEffect(() => {
    const el = surface.current;
    if (!el?.animate || reducedMotion() || menu.from !== "button") return;
    const button = target();
    // Where the Dock's left edge goes, read before the move changes it.
    const dockLeft = el.getBoundingClientRect().left;
    const grow = startNow(
      el,
      [
        button
          ? shapeOf(el, button)
          : { width: `${DOCK_HEIGHT + 1}px`, transform: "none" },
        { width: `${width}px`, transform: "none" },
      ],
      MOTION,
    );
    const items = row.current?.animate
      ? startNow(row.current, [{ opacity: 0 }, { opacity: 1 }], MOTION)
      : undefined;
    // In the same frames, what the page put on the row is squeezed from the
    // right by the growing Dock (the person, 2026-09-30).
    const squeezed = button
      ? squeeze(
          button.getBoundingClientRect().left,
          dockLeft,
          SQUEEZE_OUT,
          MOTION,
        )
      : [];
    return () => {
      grow.cancel();
      items?.cancel();
      for (const a of squeezed) a.cancel();
    };
  }, []);

  // The closing moves, set up by the effects below in the closing render and
  // started together by the last of them.
  const closingMoves = useRef<Animation[]>([]);

  // Closing to the round button, the page's things come back in the opposite
  // way, set up at once (not on the next frame: they would show whole for a
  // frame).
  // frontend-development: not a fetch; the page's things coming back.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once when closing starts
  useLayoutEffect(() => {
    const el = surface.current;
    if (!closing || !el || reducedMotion() || menu.from !== "button") return;
    const to = target();
    if (!to) return;
    const back = squeeze(
      el.getBoundingClientRect().left,
      to.getBoundingClientRect().left,
      SQUEEZE_IN,
      MOTION,
    );
    closingMoves.current.push(...back);
    return () => {
      for (const a of back) a.cancel();
    };
  }, [closing]);

  // The panel grows up and shrinks down (its content's height, measured),
  // also when it opens with the layer: from the resting Dock's "⋯" it grows
  // up out of the Dock, and from the round button it grows up while the
  // surface grows to the left, one move out of the button's corner. Taken
  // as already open, it showed whole at once (the person, 2026-10-18: "the
  // full menu just pops up; the animation is jerky").
  const panelWas = useRef(false);
  // frontend-development: not a fetch; the panel's move.
  useLayoutEffect(() => {
    const was = panelWas.current;
    panelWas.current = panel;
    if (was === panel || reducedMotion()) return;
    const full = `${panelRef.current?.offsetHeight ?? 0}px`;
    const [from, to] = panel ? ["0px", full] : [full, "0px"];
    if (!cell.current?.animate) return;
    const fold = startNow(
      cell.current,
      [{ height: from }, { height: to }],
      MOTION,
    );
    if (closing) closingMoves.current.push(fold);
  }, [panel, closing]);

  // Closing folds back to where it came from, the opening run backwards: the
  // surface, the panel and the page's things in the same frames. Started a
  // frame after the panel, the surface kept its width while the panel
  // folded, and the menu looked to sink down before it shrank (the person,
  // 2026-10-19: "why can't it do the opening backwards; it still goes
  // down").
  // - to the round button: the surface shrinks into the button's box while
  //   the items fade out (the surface itself never fades: the blur behind
  //   glass stops working while it is translucent);
  // - to the resting Dock: the surface lies over it already; only the panel
  //   folds down (it is not faded: the same Dock is under it);
  // - to neither: it waits a frame for the Dock (an item that went to
  //   another screen), and fades where it is if there is still none.
  // All of them are held at their start for two frames, then run: the first
  // frame after the closing render is a long one to draw (the screen under
  // it takes the touch back, the menu goes out of reach), and moves already
  // running went on meanwhile: the menu stood still, then jumped half way
  // (the iOS simulator, 2026-10-19; held for one frame it still jumped).
  // Then it is taken away (at once with reduced motion).
  // frontend-development: not a fetch; the closing move and its end.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once when closing starts (the opener is the layer's own)
  useLayoutEffect(() => {
    if (!closing) return;
    if (reducedMotion()) {
      onExited();
      return;
    }
    const el = surface.current;
    const moves = closingMoves.current;
    const timing = { ...MOTION, fill: "forwards" } as const;
    const fold = (last: boolean) => {
      if (!el?.animate) return true;
      const to = target();
      if (!to && !last) return false;
      const resting = to?.closest<HTMLElement>("[data-phone-resting]");
      const from = { width: `${el.offsetWidth}px`, transform: "none" };
      if (resting) moves.push(el.animate([from, shapeOf(el, resting)], timing));
      else if (to) {
        moves.push(el.animate([from, shapeOf(el, to)], timing));
        // The items' opening fade, backwards.
        const items = row.current?.animate(
          [{ opacity: 1 }, { opacity: 0 }],
          timing,
        );
        if (items) moves.push(items);
      } else moves.push(el.animate([{ opacity: 1 }, { opacity: 0 }], timing));
      return true;
    };
    const ready = fold(false);
    for (const a of moves) a.pause();
    let timer = 0;
    let frame = requestAnimationFrame(() => {
      if (!ready) fold(true);
      frame = requestAnimationFrame(() => {
        for (const a of moves) a.play();
        timer = window.setTimeout(onExited, MOTION.duration);
      });
    });
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      for (const a of moves) a.cancel();
      closingMoves.current = [];
    };
  }, [closing, onExited]);

  // Focus: from the keyboard to the current entry (or the first); by a finger
  // the dialog itself (no ring on an item).
  // frontend-development: not a fetch; where the focus goes on open.
  useEffect(() => {
    const target = pointer.current
      ? dialog.current
      : (row.current?.querySelector<HTMLElement>('[aria-current="page"]') ??
        row.current?.querySelector<HTMLElement>("a,button"));
    target?.focus({ preventScroll: true });
  }, []);
  // frontend-development: not a fetch; gives the focus back on close from the keyboard.
  useEffect(() => {
    const root = dialog.current;
    return () => {
      if (pointer.current) return;
      const active = document.activeElement;
      if (active && active !== document.body && !root?.contains(active)) return;
      (opener.isConnected ? opener : menuButton?.current)?.focus({
        preventScroll: true,
      });
    };
  }, [opener, menuButton]);

  if (!state) return null;
  return (
    <div
      ref={dialog}
      // Folding back it is only a picture of what was open.
      role="dialog"
      aria-hidden={closing || undefined}
      inert={closing || undefined}
      aria-modal="true"
      aria-label={m.phone_menu()}
      tabIndex={-1}
      data-phone-menu=""
      onPointerDownCapture={() => {
        pointer.current = true;
      }}
      onKeyDownCapture={() => {
        pointer.current = false;
      }}
      // Out of the flow (size 0): its pieces are fixed.
      className="fixed top-0 left-0 z-40 h-0 w-0 outline-none"
    >
      {/* Fixed on a wrapper (glass is `position: relative`); the surface
          grows to the left from its right edge. */}
      <div
        className="fixed flex justify-end"
        style={{ right: box.right, bottom: box.bottom, width }}
      >
        <div
          ref={surface}
          data-phone-menu-surface=""
          style={{ width }}
          className={`${GLASS} overflow-hidden rounded-[24px]`}
          data-glass={panel ? "read" : undefined}
        >
          <div
            ref={cell}
            inert={!panel || undefined}
            style={panel ? undefined : { height: 0 }}
            className="flex items-end justify-end overflow-hidden"
          >
            {panelDrawn && (
              <Panel
                ref={panelRef}
                width={width}
                bottom={box.bottom}
                all={all}
                here={here}
                place={place}
                onClose={state.closeMenu}
              />
            )}
          </div>
          <div ref={row} className="flex justify-end">
            <DockRow
              dock={dock}
              all={all}
              here={here}
              panel={panel}
              width={width}
              onMore={() => state.togglePanel()}
              onGo={state.closeMenu}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const HEADING = "px-3 pt-3 pb-1 text-[11.5px] text-faint";
const ROW =
  "relative flex h-11 w-full flex-none cursor-pointer items-center gap-3 rounded-[10px] px-3 text-left text-[15px]";
const rowTone = (on: boolean) =>
  on ? "bg-glass-current text-fg" : "text-body-soft hover:bg-glass-hover";

function EntryRow({
  entry,
  current,
  onGo,
}: {
  entry: DockEntry;
  current: boolean;
  onGo(): void;
}) {
  return (
    <EntryBadge entry={entry}>
      {(dot) => (
        <Link
          to={entry.to}
          aria-current={current ? "page" : undefined}
          onMouseDown={keepFocus}
          onClick={onGo}
          className={`${ROW} ${rowTone(current)}`}
        >
          <entry.icon className="size-5 flex-none" />
          <span className="min-w-0 flex-1 truncate">{entry.label}</span>
          {dot && (
            <span className="relative size-4 flex-none">
              <UnreadDot className="top-[4.5px]! right-[4.5px]!" />
            </span>
          )}
        </Link>
      )}
    </EntryBadge>
  );
}

function Panel({
  ref,
  width,
  bottom,
  all,
  here,
  place,
  onClose,
}: {
  ref: RefObject<HTMLDivElement | null>;
  width: number;
  /** The surface's bottom edge from the screen's. */
  bottom: string;
  all: readonly DockEntry[];
  here: DockEntry | null;
  place: DockPlace | null;
  onClose(): void;
}) {
  const { name, image: picture } = useShownAccount();
  const shell = useShellMenu();
  const navigate = useNavigate();
  const go = (to: string) => {
    onClose();
    void navigate({ to });
  };
  const accountItems = menuItemsOf(
    shell.menu.account?.menu ?? [],
    shell.select,
  );
  const me = all.filter((e) => e.group === "me");
  const theirs = all.filter((e) => e.group === "place");
  return (
    // biome-ignore lint/a11y/useSemanticElements: a list of places, not a group of fields (fieldset brings its own border and least width)
    <div
      ref={ref}
      role="group"
      aria-label={m.phone_menu_all()}
      style={{
        width,
        // The frame's height (the visible part), not the window's.
        maxHeight: `calc(var(--keyboard-frame-height, 100dvh) - ${bottom} - ${DOCK_HEIGHT + 8}px - env(safe-area-inset-top, 0px) - 16px)`,
      }}
      className="flex flex-none flex-col text-fg"
    >
      {/* What goes on (the call one is in) and the account row do not scroll
          with the list (its scrollbar ran over the surface's round corner,
          2026-09-30). */}
      <OngoingRows shape="row" onGo={onClose} className="px-2 pt-2" />
      <div className="flex h-12 flex-none items-center gap-1 px-2 pt-2">
        <MenuButton
          label={m.shell_account_menu()}
          items={accountItems}
          footer={<AccountQuickSettings />}
          className={`${ROW} w-auto! min-w-0 flex-1 text-fg hover:bg-glass-hover`}
        >
          <Avatar name={name || "?"} image={picture} size={22} />
          <span className="min-w-0 flex-1 truncate">{name}</span>
          <Icon kind="updown" size={14} className="text-faint" />
        </MenuButton>
        <button
          type="button"
          aria-label={m.phone_menu_arrange()}
          onMouseDown={keepFocus}
          onClick={() => go("/me/settings/phone-dock")}
          className="flex size-11 flex-none cursor-pointer items-center justify-center rounded-[10px] text-body-soft hover:bg-glass-hover"
        >
          <Icon kind="settings" size={18} />
        </button>
      </div>
      <div
        data-phone-menu-list=""
        className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-2 pb-1"
      >
        {me.map((e) => (
          <EntryRow
            key={e.key}
            entry={e}
            current={here?.key === e.key}
            onGo={onClose}
          />
        ))}
        <Favorites place="me" onGo={onClose} />
        {place && (
          <>
            <div className="mx-3 my-2 h-px flex-none bg-field" />
            <MenuButton
              label={m.phone_place_menu({ name: place.name })}
              items={menuItemsOf(shell.menu.place?.menu ?? [], shell.select)}
              className={`${ROW} text-fg hover:bg-glass-hover`}
            >
              <span className="flex size-5 flex-none items-center justify-center">
                {place.mark}
              </span>
              <span className="min-w-0 flex-1 truncate">{place.name}</span>
              <Icon kind="updown" size={14} className="text-faint" />
            </MenuButton>
            {theirs.map((e) => (
              <EntryRow
                key={e.key}
                entry={e}
                current={here?.key === e.key}
                onGo={onClose}
              />
            ))}
            <Favorites place={place.id} onGo={onClose} />
            {menuLists(shell.menu).map((list) =>
              list.links.length === 0 ? null : (
                <Fragment key={list.heading}>
                  <div className={HEADING}>{list.heading}</div>
                  {list.links.map((l) => (
                    <Link
                      key={l.id}
                      to={(l.to ?? "/me") as never}
                      onMouseDown={keepFocus}
                      onClick={onClose}
                      activeProps={{
                        "aria-current": "page",
                        className: "bg-glass-current text-fg",
                      }}
                      className={`${ROW} text-body-soft hover:bg-glass-hover`}
                    >
                      <span className="min-w-0 flex-1 truncate">{l.label}</span>
                    </Link>
                  ))}
                </Fragment>
              ),
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The favorites of a place (Me's, or the workspace's: its place id is the
 * favorites' place), to go to only (folders are arranged on a wide screen).
 */
function Favorites({ place, onGo }: { place: string; onGo(): void }) {
  const { favoriteKinds } = useMeApp();
  const list = useFavorites(place);
  const items = (list.data?.root ?? [])
    .flatMap((e) => (e.type === "folder" ? e.items : [e]))
    .filter((i) => i.kind in favoriteKinds);
  if (items.length === 0) return null;
  return (
    <>
      <div className={HEADING}>{m.shell_favorites()}</div>
      {items.map((i) => {
        const kind = favoriteKinds[i.kind];
        if (!kind) return null;
        const KindIcon = kind.icon;
        return (
          <Link
            key={i.id}
            to={kind.to(i.targetId)}
            onMouseDown={keepFocus}
            onClick={onGo}
            activeProps={{
              "aria-current": "page",
              className: "bg-glass-current text-fg",
            }}
            className={`${ROW} text-body-soft hover:bg-glass-hover`}
          >
            <KindIcon className="size-5 flex-none" />
            <span className="min-w-0 flex-1 truncate">{i.title}</span>
          </Link>
        );
      })}
    </>
  );
}
